import React from "react";
import { inject, observer } from "mobx-react";
import styles from "./index.module.scss";
import classNames from "classnames";
import CommonPopup from "@components/commonPopup";
import CommonAlert from "@components/commonAlert";
import Switch from "@components/switch";
import { BASE_IMG_URL } from '@images/imageUrls';
import type { ThreadAction } from '@stores/thread/action';
import type { UserAction } from '@stores/user/action';
import { Radio, Input } from "antd";
import StickyDatePicker from "./StickyDatePicker";
import Toast from '@components/Toast';
import { getManageOperationSuccessMessage } from '../operationMessage';
const { TextArea } = Input;
import dayjs from 'dayjs';
import 'dayjs/locale/zh-cn';
dayjs.locale('zh-cn');

// 检查定时是否过期
const isScheduleExpired = (fixedAt: any): boolean => {
    if (!fixedAt) return true;
    return dayjs(fixedAt).isBefore(dayjs());
};

// 简化接口定义
interface H5ManagePermissionsProps {
  thread?: ThreadAction;
  // 基础属性
  id: any;
  threadData: any
  className?: string;
  type?: number; // 帖子类型，1为图文和动态

  // 状态属性
  isSelf?: boolean;
  isApproved?: boolean;
  isEssence?: boolean; // 是否加精
  isSticky?: boolean | number; // 是否置顶
  isSink?: boolean; // 是否沉帖
  isHidden?: boolean; // 是否隐藏
  isDisplay?: boolean; // 是否屏蔽
  //   isHot?: boolean;  // 是否热门
  isRecommend?: boolean; // 是否推荐
  //   isProhibited?: boolean;   // 禁止评论
  stickyType?: any; // 置顶类型
  stickyAt?: any; // 置顶时间
  showIndexAt?: any; // 首页置顶时间
  showIndex?: boolean;

  // 权限控制
  canEdit?: boolean;
  canDelete?: boolean;
  canEssence?: boolean;
  canStick?: boolean;
  //   canSolve?: boolean;
  canSink?: boolean;
  canHidden?: boolean;
  canDisplay?: boolean;
  //   canHot?: boolean;
  canRecommend?: boolean;
  //   canProhibitedComment?: boolean;

  // 操作函数
  handleSetState?: any;
  handleEdit?: Function; // 编辑
  handleDelete?: Function; // 确认删除
  handleEssence?: Function; // 精华
  handleStick?: Function; // 确认置顶
  //   handleSolve?: Function;  // 帮他处理
  handleSink?: Function; // 沉帖
  handleHidden?: Function; // 隐藏本帖
  handleDisplay?: Function; // 屏蔽
  //   handleHot?: Function;  // 热门
  handleSite?: Function; // 推荐
  //   handleProhibited?: Function; // 禁止评论
  handleReport?: Function; // 确认举报
  handelManageClose?: Function; // 关闭管理弹框
  getThreadDetail?: Function  // 获取详情接口数据，用于更新状态

  // MobX stores (可选)
  app?: any;
  site?: any;
  user?: UserAction;
}
interface H5ManagePermissionsState {
  isEssenceStatus: any;
  isStickStatus: boolean | number;
  isSinkStatus: boolean;
  isHiddenStatus: boolean;
  isDisplayStatus: boolean;
  // isHotStatus: boolean;
  isRecommendStatus: boolean;
  // isProhibitedStatus: boolean;
  stickyPopupStatus: any;
  stickyTypeValue: any;
  stickyTopicIdValue: any;
  stickyAtValue: any;
  showIndexAtValue: any;
  deletePopupStatus: any;
  reportPopupStatus: any;
  reportReasonList: any;
  reportReason: any;
  reportDescription: any;
}

@inject("app", "site", "user", "thread")
@observer
class H5ManagePermissions extends React.Component<
  H5ManagePermissionsProps,
  H5ManagePermissionsState
> {
  static defaultProps = {
    isApproved: true,
    className: "",
  };
  constructor(props: H5ManagePermissionsProps) {
    super(props);
    const stickyTypeValue = this.getStickyTypeValueFromProps(props);
    const initialStickyTopicId: string[] = Array.isArray(props.threadData?.stickyTopics) && props.threadData.stickyTopics.length > 0
      ? props.threadData.stickyTopics.map((t: any) => String(t?.id ?? t?.name ?? t?.title ?? '')).filter(Boolean)
      : [];

    // 从 props 中获取初始值设置到 state
    this.state = {
      isEssenceStatus: props.isEssence || false,
      isStickStatus: stickyTypeValue > 0,
      isSinkStatus: props.isSink || false,
      isHiddenStatus: props.isHidden || false,
      isDisplayStatus: props.isDisplay || false,
      isRecommendStatus: props.isRecommend || false,
      stickyPopupStatus: false, // 置顶弹框
      stickyTypeValue: stickyTypeValue, // 置顶类型 1=话题 2=首页 3=同时
      stickyTopicIdValue: initialStickyTopicId,
      stickyAtValue: props.stickyAt && dayjs(props.stickyAt).isValid() ? props.stickyAt : '', // 置顶时间
      showIndexAtValue: props.threadData?.showIndexAt && dayjs(props.threadData.showIndexAt).isValid() ? props.threadData.showIndexAt : '', // 首页置顶时间
      deletePopupStatus: false, // 删除二次确认弹框
      reportPopupStatus: false, // 举报弹框
      reportReasonList: [
        { value: "淫秽色情", label: "淫秽色情" },
        { value: "垃圾广告", label: "垃圾广告" },
        { value: "暴力恐怖", label: "暴力恐怖" },
        { value: "违规传播", label: "违规传播" },
        { value: "政治谣言", label: "政治谣言" },
        { value: "谩骂侮辱", label: "谩骂侮辱" },
        { value: "灌水发帖/评论", label: "灌水发帖/评论" },
        { value: "其他", label: "其他" },
      ],
      reportReason: "", // 举报理由
      reportDescription: "", // 举报描述
    };
  }

  private getStickyTypeValueFromProps = (props: H5ManagePermissionsProps): number => {
    const rawStickyType = Number(props.stickyType);
    const baseStickyType = Number.isFinite(rawStickyType) && rawStickyType > 0
      ? rawStickyType
      : (typeof props.isSticky === 'number' ? props.isSticky : (props.isSticky ? 1 : 0));

    return props.threadData?.showIndex === true ? (baseStickyType | 2) : baseStickyType;
  };

  // 组件更新时同步 props 到 state
  componentDidUpdate(prevProps: H5ManagePermissionsProps) {
    // 如果 props 中的状态值发生变化，更新 state
    if (prevProps.isEssence !== this.props.isEssence) {
      this.setState({ isEssenceStatus: this.props.isEssence || false });
    }
    if (
      prevProps.isSticky !== this.props.isSticky ||
      prevProps.stickyType !== this.props.stickyType ||
      prevProps.threadData?.showIndex !== this.props.threadData?.showIndex
    ) {
      const stickyTypeValue = this.getStickyTypeValueFromProps(this.props);
      this.setState({
        isStickStatus: stickyTypeValue > 0,
        stickyTypeValue,
        stickyTopicIdValue: this.getInitialStickyTopicId(),
      });
    }
    if (prevProps.isSink !== this.props.isSink) {
      this.setState({ isSinkStatus: this.props.isSink || false });
    }
    if (prevProps.isHidden !== this.props.isHidden) {
      this.setState({ isHiddenStatus: this.props.isHidden || false });
    }
    if (prevProps.isDisplay !== this.props.isDisplay) {
      this.setState({ isDisplayStatus: this.props.isDisplay || false });
    }
    if (prevProps.isRecommend !== this.props.isRecommend) {
      this.setState({ isRecommendStatus: this.props.isRecommend || false });
    }
    if (prevProps.stickyAt !== this.props.stickyAt) {
      this.setState({ stickyAtValue: this.props.stickyAt && dayjs(this.props.stickyAt).isValid() ? this.props.stickyAt : '' });
    }
    if (prevProps.threadData?.showIndexAt !== this.props.threadData?.showIndexAt) {
      this.setState({ showIndexAtValue: this.props.threadData?.showIndexAt && dayjs(this.props.threadData.showIndexAt).isValid() ? this.props.threadData.showIndexAt : '' });
    }
  }

  // 处理操作函数，同时更新本地状态
  // 加精
  private handleEssenceClick = () => {
    const { handleEssence } = this.props;
    // this.setState((prevState) => ({
    //     isEssenceStatus: !prevState.isEssenceStatus
    // }));
    handleEssence && handleEssence();
  };

  // 置顶，弹出置顶弹框
  private handleStickClick = () => {
    const { stickyAt, threadData } = this.props;
    this.setState((prevState) => ({
      stickyPopupStatus: true,
      stickyTopicIdValue: (Array.isArray(prevState.stickyTopicIdValue) && prevState.stickyTopicIdValue.length > 0) ? prevState.stickyTopicIdValue : this.getInitialStickyTopicId(),
      stickyAtValue: stickyAt || prevState.stickyAtValue,
      showIndexAtValue: threadData?.showIndexAt || prevState.showIndexAtValue,
    }));
  };
  // 沉帖
  private handleSinkClick = () => {
    const { handleSink } = this.props;
    this.setState((prevState) => ({
      isSinkStatus: !prevState.isSinkStatus,
    }));
    handleSink && handleSink();
  };
  // 隐藏本帖
  private handleHiddenClick = () => {
    const { handleHidden } = this.props;
    this.setState((prevState) => ({
      isHiddenStatus: !prevState.isHiddenStatus,
    }));
    handleHidden && handleHidden();
  };
  // 屏蔽
  private handleDisplayClick = () => {
    const { handleDisplay } = this.props;
    this.setState((prevState) => ({
      isDisplayStatus: !prevState.isDisplayStatus,
    }));
    handleDisplay && handleDisplay();
  };
  // 推荐
  private handleSiteClick = () => {
    const { handleSite } = this.props;
    this.setState((prevState) => ({
      isRecommendStatus: !prevState.isRecommendStatus,
    }));
    handleSite && handleSite();
  };
  // 编辑
  private handleEditClick = () => {
    const { handleEdit } = this.props;
    // this.props.history && this.props.history.push(`/topicDetail/${id}`);
    handleEdit && handleEdit();
  };
  // 删除，弹出删除弹框
  private handleDeleteClick = () => {
    const { handleDelete, type } = this.props;
    this.setState((prevState) => ({
      deletePopupStatus: !prevState.deletePopupStatus,
    }));
    // handleDelete && handleDelete(type, 'thread');
  };

  // 举报，弹出举报弹框
  private handleReportClick = () => {
    this.setState((prevState) => ({
      reportPopupStatus: !prevState.reportPopupStatus,
    }));
  };

  // 选择置顶类型
  private handleStickySwitch = (type: number) => {
    this.setState((prevState) => ({
      stickyTypeValue: (prevState.stickyTypeValue & type) ? (prevState.stickyTypeValue & ~type) : (prevState.stickyTypeValue | type),
      stickyTopicIdValue: type === 1 && (prevState.stickyTypeValue & type) ? [] : prevState.stickyTopicIdValue,
      stickyAtValue: (() => {
        const nextStickyTypeValue = (prevState.stickyTypeValue & type) ? (prevState.stickyTypeValue & ~type) : (prevState.stickyTypeValue | type);
        return type === 1 && nextStickyTypeValue !== 0 ? prevState.stickyAtValue : (nextStickyTypeValue === 0 ? '' : prevState.stickyAtValue);
      })(),
      showIndexAtValue: (() => {
        const nextStickyTypeValue = (prevState.stickyTypeValue & type) ? (prevState.stickyTypeValue & ~type) : (prevState.stickyTypeValue | type);
        return type === 2 && nextStickyTypeValue !== 0 ? prevState.showIndexAtValue : (nextStickyTypeValue === 0 ? '' : prevState.showIndexAtValue);
      })(),
    }));
  };

  private getStickyTopics = () => {
    const { threadData } = this.props;
    return Array.isArray(threadData?.topics) ? threadData.topics : [];
  };

  private getInitialStickyTopicId = (): string[] => {
    const { threadData } = this.props;
    if (Array.isArray(threadData?.stickyTopics) && threadData.stickyTopics.length > 0) {
      return threadData.stickyTopics.map((t: any) => String(t?.id ?? t?.name ?? t?.title ?? '')).filter(Boolean);
    }
    return [];
  };

  private handleStickyTopicSelect = (topic: any) => {
    const topicValue = String(topic?.id ?? topic?.name ?? topic?.title ?? '');
    this.setState((prevState) => {
      const currentIds: string[] = Array.isArray(prevState.stickyTopicIdValue) ? prevState.stickyTopicIdValue : [];
      const idx = currentIds.indexOf(topicValue);
      const nextIds = idx >= 0
        ? currentIds.filter((id: string) => id !== topicValue)
        : [...currentIds, topicValue];
      return { stickyTopicIdValue: nextIds };
    });
  };

  private handleStickyConfirm = () => {
    const { stickyTypeValue, stickyTopicIdValue, stickyAtValue, showIndexAtValue } = this.state;
    const isTopicSticky = (stickyTypeValue & 1) === 1;
    const isGlobalSticky = (stickyTypeValue & 2) === 2;
    const selectedTopicIds: string[] = Array.isArray(stickyTopicIdValue) ? stickyTopicIdValue : [];

    if (isTopicSticky && selectedTopicIds.length === 0) {
      Toast.info({ content: '请选择1个话题' });
      return;
    }

    if (isTopicSticky && !stickyAtValue) {
      Toast.info({ content: '请选择话题置顶截止时间' });
      return;
    }

    if (isGlobalSticky && !showIndexAtValue) {
      Toast.info({ content: '请选择首页置顶截止时间' });
      return;
    }

    this.createThreadOpera('isSticky', stickyTypeValue);
  };

  private getTopicBadge = (topic: any) => {
    if (topic?.isHot) {
      return { text: '热', img: 'common/topic_hot.png' };
    }
    if (topic?.isNew) {
      return { text: '新', img: 'common/topic_new.png' };
    }
    if (topic?.isRecommended) {
      return { text: '荐', img: 'common/topic_recommend.png' };
    }
    return null;
  };

  // 选择置顶时间
  private onChangeStickyAt = (date: any, dateString: any) => {
    if (date && date.isValid()) {
      this.setState({ stickyAtValue: date.format('YYYY-MM-DD HH:mm') });
    } else {
      this.setState({ stickyAtValue: '' });
    }
  };

  private onChangeShowIndexAt = (date: any, dateString: any) => {
    if (date && date.isValid()) {
      this.setState({ showIndexAtValue: date.format('YYYY-MM-DD HH:mm') });
    } else {
      this.setState({ showIndexAtValue: '' });
    }
  };

  // 确认置顶
  stickyConfirm = () => {
    // 调用置顶接口
    const { handleStick } = this.props;
    this.setState((prevState) => ({
      isStickStatus: !prevState.isStickStatus,
    }));
    handleStick && handleStick();
    this.closePopup();
  };

  // 确认删除
  private deletThreadConfirm = () => {
    // 删除确认逻辑
    const { handleDelete } = this.props;
    handleDelete && handleDelete();
    this.closePopup();
  };

  // 复制预览链接
  private copyPreviewLink = () => {
    const { threadData, id } = this.props;
    
    if (!threadData?.code || !id) {
      Toast.info({ content: '链接信息不完整，无法复制' });
      return;
    }

    // 从当前URL动态获取域名
    const origin = window.location.origin;
    const url = `${origin}/threadDetail/${id}?code=${threadData.code}`;
    
    // 使用 navigator.clipboard API 复制到剪贴板
    navigator.clipboard.writeText(url).then(() => {
      Toast.success({ content: '链接已复制' });
    }).catch(() => {
      // 如果 clipboard API 不可用，使用备用方案
      const textArea = document.createElement('textarea');
      textArea.value = url;
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        Toast.success({ content: '链接已复制' });
      } catch (err) {
        Toast.info({ content: '复制失败，请手动复制' });
      }
      document.body.removeChild(textArea);
    });
  };

  // 提交举报并同步关闭管理面板
  private handleReportConfirm = () => {
    const { handleReport, id, handelManageClose, thread } = this.props;
    const { reportReason } = this.state;

    handleReport && handleReport();
    if (!reportReason) {
      Toast.info('请选择举报理由');
      return;
    }

    if (thread) {
      const params = {
        id,
        type: 1, // 0 个人主页 1 主题 2 评论/回复 3 问题反馈 4 活动 5 私信
        reason: reportReason,
      };
      thread.createReports(params).then(() => {
        Toast.info('举报成功');
        handelManageClose && handelManageClose();
      });
    }
    this.closePopup();
  };

  // 关闭弹框
  private closePopup = () => {
    this.setState({
      stickyPopupStatus: false,
      reportPopupStatus: false,
      deletePopupStatus: false
    });
  };

  // 帖子置顶、取消置顶，沉帖，精华，屏蔽，推荐，删除
  createThreadOpera = (val, status = true) => {
    const { thread, id, handelManageClose, getThreadDetail } = this.props;
  //   if(val === 'isRecommend' && isEssence && !isRecommend) {    // 如果是推荐，且已加精，不允许开启推荐
  //     Toast.info({content: '帖子已加精，不能同时推荐'});
  //     return;
  // }
  // if(val === 'isEssence' && isRecommend && !isEssence) {    // 如果是加精，且已推荐，不允许开启加精
  //     Toast.info({content: '帖子已推荐，不能同时加精'});
  //     return;
  // }
    let { stickyAtValue, showIndexAtValue, stickyTypeValue, stickyTopicIdValue } = this.state;
    if (thread) {
      let params: any = {};
      if(val == 'isSticky') {
        const isTopicSticky = (stickyTypeValue & 1) === 1;
        const isGlobalSticky = (stickyTypeValue & 2) === 2;
        const selectedTopicIds: string[] = Array.isArray(stickyTopicIdValue) ? stickyTopicIdValue : [];
        const stickyTopicIds = isTopicSticky ? selectedTopicIds : [];
        params = {
            id,
            showIndex: isGlobalSticky,
            showIndexAt: isGlobalSticky ? (showIndexAtValue || '') : '',
        };
        if (isTopicSticky) {
          params.isSticky = true;
          params.stickyType = 1;
          params.stickyAt = stickyAtValue || '';
          params.stickyTopicIds = stickyTopicIds;
        } else if (isGlobalSticky) {
          params.isSticky = false;
          params.stickyType = 0;
          params.stickyAt = '';
          params.stickyTopicIds = [];
        } else {
          params.isSticky = false;
          params.stickyType = 0;
          params.stickyAt = '';
          params.showIndexAt = '';
          params.stickyTopicIds = [];
        }
    } else {
        params = {
            [val]: !status,
            id: id
        }
    }
      // 可选：添加 type
      // if (type) {
      //     params.type = type;
      // }
      thread.createThreadOpera(params).then(res => {
        if (res.code == 0) {
          Toast.success({
            content: getManageOperationSuccessMessage(val, status, stickyTypeValue)
          });
          if(val == 'isSticky') { // 如果是置顶，需要关闭置顶弹框
            this.setState({
                stickyPopupStatus: false
            })
          }
          getThreadDetail && getThreadDetail();
        }
        handelManageClose && handelManageClose()
      })
    }
  }

  // 检查是否有权限
  private hasPermissions = (): boolean => {
    const props = this.props;
    return !!(
      props.canEdit ||
      props.canDelete ||
      props.canEssence ||
      props.canStick ||
      props.canSink ||
      props.canHidden ||
      props.canDisplay ||
      props.canRecommend
    );
  };

  render() {
    console.log('管理');
    const props = this.props;
    // if (!this.hasPermissions()) {
    //   return null;
    // }
    // 解构
    const {
      stickyPopupStatus,
      stickyTypeValue,
      stickyTopicIdValue,
      stickyAtValue,
      showIndexAtValue,
      deletePopupStatus,
      reportPopupStatus,
      reportReasonList,
      reportReason
    } = this.state;
    const stickyTopics = this.getStickyTopics();
    const isTopicSticky = (stickyTypeValue & 1) === 1;
    const isGlobalSticky = (stickyTypeValue & 2) === 2;
    const showStickyTopicList = isTopicSticky;
    const stickyConfirmDisabled = (isTopicSticky && !stickyAtValue) || (showStickyTopicList && (!Array.isArray(stickyTopicIdValue) || stickyTopicIdValue.length === 0)) || (isGlobalSticky && !showIndexAtValue);

    return (
      <div>
        {/* {props.title && <SidebarTitle title={props.title} />} */}
        <div className={`${props.className} ${styles.manageListBox}`}>
          <div className={styles.manageMask} onClick={() =>
            props.handelManageClose && props.handelManageClose()
          }></div>
          
          <div className={styles.manageBox}>
            {/* <div className={styles.manageTitle}>管理</div> */}
            <div className={styles.manageGap}></div>
            <div className={styles.manageContent}>
            {/* 编辑、删除、举报属于同一个操作模块。 */}
            <div className={styles.manageGroup}>
            {props.canEdit && (
              <div className={styles.manageBtn} onClick={() => this.handleEditClick()}>
                <div className={styles.manageBtnLeft}>
                  <img src={`${BASE_IMG_URL}/detail/manage5.png`} alt="" className={styles.manageIcon} />
                  <span className={styles.manageText}>编辑</span>
                </div>
                <img src={`${BASE_IMG_URL}/detail/jtr2.png`} alt="" className={styles.manageActionIcon} />
              </div>
            )}
            {props.canDelete && (
              <div className={classNames(styles.manageBtn, styles.deleteBtn)} onClick={() => this.handleDeleteClick()}>
                <div className={styles.manageBtnLeft}>
                  <img src={`${BASE_IMG_URL}/detail/manage6.png`} alt="" className={styles.manageIcon} />
                  <span className={styles.manageText}>删除</span>
                </div>
                <img src={`${BASE_IMG_URL}/detail/jtr2.png`} alt="" className={styles.manageActionIcon} />
              </div>
            )}
            {/* 举报入口不受帖子管理权限控制，点击前统一校验登录状态。 */}
            <div className={styles.manageBtn} onClick={() => window.globalEnsureLogin(this.handleReportClick)}>
              <div className={styles.manageBtnLeft}>
                <img src={`${BASE_IMG_URL}/detail/manage9.png`} alt="" className={styles.manageIcon} />
                <span className={styles.manageText}>举报</span>
              </div>
              <img src={`${BASE_IMG_URL}/detail/jtr2.png`} alt="" className={styles.manageActionIcon} />
            </div>
            {/* 定时帖子才显示复制链接，作为点击类操作放在编辑/删除之后。 */}
            {!!(props.threadData?.code && props.threadData?.fixedAt && !isScheduleExpired(props.threadData.fixedAt)) && (
              <div className={styles.manageBtn} onClick={() => this.copyPreviewLink()}>
                <div className={styles.manageBtnLeft}>
                  <img  src={`${BASE_IMG_URL}/detail/manage12.png`} alt="" className={styles.manageIcon} />
                  <span className={styles.manageText}>复制链接</span>
                </div>
                <img src={`${BASE_IMG_URL}/detail/jtr2.png`} alt="" className={styles.manageActionIcon} />
              </div>
            )}
            </div>
            {/* 置顶及其他权限开关属于第二个操作模块。 */}
            <div className={classNames(styles.manageGroup, styles.manageGroup2)}>
            {!!(props.isApproved && props.canStick) && (
              <div className={styles.manageBtn} onClick={() => this.handleStickClick()}>
                <div className={styles.manageBtnLeft}>
                  <img src={`${BASE_IMG_URL}/detail/manage1.png`} alt="" className={styles.manageIcon} />
                  <span className={styles.manageText}>{this.state.isStickStatus ? '取消' : ''}置顶</span>
                </div>
                <img src={`${BASE_IMG_URL}/detail/jtr2.png`} alt="" className={styles.manageActionIcon} />
              </div>
            )}
            {!!(props.isApproved && props.canEssence) && (
              <div className={styles.manageBtn}>
                <div className={styles.manageBtnLeft}>
                  <img src={`${BASE_IMG_URL}/detail/manage3.png`} alt="" className={styles.manageIcon} />
                  <span className={styles.manageText}>加精</span>
                </div>
                <Switch checked={!!props.isEssence} onChange={() => this.createThreadOpera('isEssence', props.isEssence)} aria-label="精华" />
              </div>
            )}
            {!!(props.isApproved && props.canRecommend) && (
              <div className={styles.manageBtn}>
                <div className={styles.manageBtnLeft}>
                  <img src={`${BASE_IMG_URL}/detail/manage4.png`} alt="" className={styles.manageIcon} />
                  <span className={styles.manageText}>推荐</span>
                </div>
                <Switch checked={!!props.isRecommend} onChange={() => this.createThreadOpera('isRecommend', props.isRecommend)} aria-label="推荐" />
              </div>
            )}
            {!!(props.isApproved && props.canSink) && (
              <div className={styles.manageBtn}>
                <div className={styles.manageBtnLeft}>
                  <img src={`${BASE_IMG_URL}/detail/manage2.png`} alt="" className={styles.manageIcon} />
                  <span className={styles.manageText}>沉帖</span>
                </div>
                <Switch checked={!!props.isSink} onChange={() => this.createThreadOpera('isSink', props.isSink)} aria-label="沉帖" />
              </div>
            )}
            {!!(props.isApproved &&
              props.canDisplay &&
              props.type !== undefined &&
              props.type < 2) && (
                <div className={styles.manageBtn}>
                  <div className={styles.manageBtnLeft}>
                    <img src={`${BASE_IMG_URL}/detail/manage7.png`} alt="" className={styles.manageIcon} />
                    <span className={styles.manageText}>屏蔽</span>
                  </div>
                  <Switch checked={!props.isDisplay} onChange={() => this.createThreadOpera('isDisplay', props.isDisplay)} aria-label="屏蔽" />
                </div>
              )}
            {!!(props.isApproved && props.canHidden) && (
              <div className={styles.manageBtn}>
                <div className={styles.manageBtnLeft}>
                  <img src={`${BASE_IMG_URL}/detail/manage8.png`} alt="" className={styles.manageIcon} />
                  <span className={styles.manageText}>仅自己可见</span>
                </div>
                <Switch checked={!!props.isHidden} onChange={() => this.createThreadOpera('isHidden', props.isHidden)} aria-label="仅自己可见" />
              </div>
            )}
            </div>
            {/* {props.isApproved && props.canHot && (
                    <div className={styles.manageBtn} onClick={props.handleHot}>
                    {props.isHot ? '取消' : ''}热门
                    </div>
                )} */}

            {/* {props.isApproved && props.canProhibitedComment && (
                    <div className={styles.manageBtn} onClick={props.handleProhibited}>
                    {props.isProhibited ? '允许' : '禁止'}评论
                    </div>
                )} */}

            {/* {props.isApproved && props.canSolve && (
                    <div className={styles.manageBtn} onClick={props.handleSolve}>
                    帮TA处理
                    </div>
                )} */}
            </div>
          </div>
        </div>

        {/* 置顶弹框 */}
        {stickyPopupStatus && (
          <div className={styles.h5StickyPopupMask} onClick={() => this.closePopup()}>
            <div className={styles.h5StickyPopupPanel} onClick={(e) => e.stopPropagation()}>
              <div className={styles.h5StickyPopupTitle}>置顶设置</div>

                <div className={styles.h5StickyCardList}>
                <div className={styles.h5StickyCard}>
                  <div className={styles.h5StickyCardTop}>
                    <div className={styles.h5StickyCardLeft}>
                      <img
                        className={styles.h5StickyCardIconImg}
                        src={`${BASE_IMG_URL}/detail/icon_home.png`}
                        alt=""
                      />
                      <div className={styles.h5StickyCardTextBox}>
                        <div className={styles.h5StickyCardLabel}>首页置顶</div>
                        <div className={styles.h5StickyCardDesc}>在精选页中置顶</div>
                      </div>
                    </div>
                    <Switch checked={isGlobalSticky} onChange={() => this.handleStickySwitch(2)} aria-label="首页置顶" />
                  </div>

                  {isGlobalSticky && (
                    <>
                      <div className={styles.h5StickyDivider} />
                      <div className={styles.h5StickyDateCard}>
                        <div className={styles.h5StickyDateLeft}>
                          <img
                            className={styles.h5StickyCardIconImg}
                            src={`${BASE_IMG_URL}/detail/icon_clock.png`}
                            alt=""
                          />
                          <div className={styles.h5StickyCardLabel}>首页置顶截止时间</div>
                        </div>
                        <StickyDatePicker
                          title="首页置顶截止时间"
                          value={showIndexAtValue}
                          onChange={this.onChangeShowIndexAt}
                        />
                      </div>
                    </>
                  )}
                </div>

                <div className={classNames(styles.h5StickyCard, showStickyTopicList ? styles.h5StickyCardExpanded : "")}>
                  <div className={styles.h5StickyCardTop}>
                    <div className={styles.h5StickyCardLeft}>
                      <img
                        className={styles.h5StickyCardIconImg}
                        src={`${BASE_IMG_URL}/detail/icon_topic_hash.png`}
                        alt=""
                      />
                      <div className={styles.h5StickyCardTextBox}>
                        <div className={styles.h5StickyCardLabel}>话题置顶</div>
                        <div className={styles.h5StickyCardDesc}>在所属话题中置顶</div>
                      </div>
                    </div>
                    <Switch checked={isTopicSticky} onChange={() => this.handleStickySwitch(1)} aria-label="话题置顶" />
                  </div>

                  {showStickyTopicList && (
                    <>
                      <div className={styles.h5StickyDivider} />
                      <div className={styles.h5StickyTopicList}>
                        {stickyTopics.map((topic: any, idx: number) => {
                          const badge = this.getTopicBadge(topic);
                          const topicValue = String(topic?.id ?? topic?.name ?? topic?.title ?? '');
                          const selectedIds: string[] = Array.isArray(stickyTopicIdValue) ? stickyTopicIdValue : [];
                          const isTopicSelected = selectedIds.includes(topicValue);
                          return (
                            <button
                              type="button"
                              key={topic?.id ?? `h5-sticky-topic-${idx}`}
                              className={classNames(styles.h5StickyTopicChip, isTopicSelected ? styles.h5StickyTopicChipActive : '')}
                              onClick={() => this.handleStickyTopicSelect(topic)}
                            >
                              <span className={styles.h5StickyTopicName}>
                                <img
                                  className={styles.h5StickyTopicHashIcon}
                                    src={`${BASE_IMG_URL}/${isTopicSelected
                                      ? 'detail/topic_icon2.png'
                                      : 'detail/topic_icon1.png'}`}
                                  alt="#"
                                />
                                {topic?.name || topic?.title || '未命名话题'}
                              </span>
                              {badge && (
                                <img
                                  className={styles.h5StickyTopicBadgeImg}
                                   src={`${BASE_IMG_URL}/${badge.img}`}
                                  alt={badge.text}
                                />
                              )}
                            </button>
                          );
                        })}
                      </div>
                    </>
                  )}
                </div>

                {isTopicSticky && (
                  <div className={styles.h5StickyDateCard}>
                    <div className={styles.h5StickyDateLeft}>
                      <img
                        className={styles.h5StickyCardIconImg}
                        src={`${BASE_IMG_URL}/detail/icon_clock.png`}
                        alt=""
                      />
                      <div className={styles.h5StickyCardLabel}>话题置顶截止时间</div>
                    </div>
                    <StickyDatePicker
                      title="话题置顶截止时间"
                      value={stickyAtValue}
                      onChange={this.onChangeStickyAt}
                    />
                  </div>
                )}
              </div>

              <div className={styles.h5StickyFooter}>
                <button type="button" className={styles.h5StickyCancel} onClick={() => this.closePopup()}>
                  取消
                </button>
                <button
                  type="button"
                  className={classNames(styles.h5StickyConfirm, stickyConfirmDisabled ? styles.h5StickyConfirmDisabled : "")}
                  onClick={this.handleStickyConfirm}
                >
                  确定
                </button>
              </div>
            </div>
          </div>
        )}

        {/* 删除二次确认弹框 */}
        <CommonAlert
          visible={deletePopupStatus}
          title='帖子删除后无法撤销，请确认是否删除'
          onCancelChange={() => this.closePopup()}
          onHidePopup={() => this.closePopup()}
          onConfirmButtonChange={() => this.createThreadOpera('isDeleted', false)}
        >
        </CommonAlert>

        {/* 举报弹框：选择举报理由后提交主题举报。 */}
        <CommonPopup
          visible={reportPopupStatus}
          title="举报"
          onCancelChange={() => this.closePopup()}
          onConfirmButtonChange={() => this.handleReportConfirm()}
        >
          <div className={styles.reportPopupSlotBox}>
            <div className={styles.reportText}>
              <span className={styles.must}>*</span>举报理由
              <span>请选择举报理由</span>
            </div>
            <div className={styles.reportReasonListBox}>
              <Radio.Group
                name="reportReason"
                value={reportReason}
                onChange={(e) => this.setState({ reportReason: e.target.value })}
                options={reportReasonList}
              />
            </div>
            <div className={styles.reportText}>举报描述</div>
            <TextArea rows={4} placeholder="请输入举报理由" maxLength={600} />
          </div>
        </CommonPopup>

      </div>
    );
  }
}

export default H5ManagePermissions;
